import { useState, useCallback } from 'react';
import { AppButton } from '@/components/atoms';
import { CollapsibleCard } from '@/components/molecules';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import {
  useReturnRezeptList,
  useMarkReturn,
  useCancelReturn,
  useUpdateResubmission,
} from '@/hooks/main/useReturnRezept';
import ReturnRezeptTable, { isRowActionable } from './components/ReturnRezeptTable';
import ResubmissionMonthModal from './components/ResubmissionMonthModal';
import AddReturnModal from './components/AddReturnModal';
import type { ReturnRezeptRow } from '@/services/main/returnRezept/types';
import { AxiosError } from 'axios';

const ALERT_NO_SELECTION = '対象のレセプトを選択してください。';

export default function ReturnInitPage() {
  const toast = useAppToast();
  const appModal = useAppModal();

  const { data, isLoading } = useReturnRezeptList();
  const { markReturnMutation, isLoading: markPending } = useMarkReturn();
  const { cancelReturnMutation } = useCancelReturn();
  const { updateResubmissionMutation, isLoading: resubmitPending } = useUpdateResubmission();

  const list: ReturnRezeptRow[] = data?.rezepts ?? [];

  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [resubmissionModalOpen, setResubmissionModalOpen] = useState(false);
  const [resubmissionRezeptIds, setResubmissionRezeptIds] = useState<number[]>([]);
  const [addReturnModalOpen, setAddReturnModalOpen] = useState(false);
  const [violatingPatients, setViolatingPatients] = useState<
    Array<{ serial_number: string | number; patient_name: string }>
  >([]);
  const [serverError, setServerError] = useState('');

  const handleToggleRow = useCallback((id: number, checked: boolean) => {
    setSelectedRowKeys(prev => (checked ? [...prev, id] : prev.filter(x => x !== id)));
  }, []);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      const selectableIds = list.filter(isRowActionable).map(r => r.id);
      setSelectedRowKeys(checked ? selectableIds : []);
    },
    [list]
  );

  const handleBulkResubmission = useCallback(() => {
    if (selectedRowKeys.length === 0) {
      appModal.warning({ title: ALERT_NO_SELECTION });
      return;
    }

    const hasExistingYearmonth = list
      .filter(r => selectedRowKeys.includes(r.id))
      .some(r => r.rezept_yearmonth !== null && r.rezept_yearmonth_jp !== '');

    const openModal = () => {
      setResubmissionRezeptIds(selectedRowKeys);
      setViolatingPatients([]);
      setServerError('');
      setResubmissionModalOpen(true);
    };

    if (hasExistingYearmonth) {
      appModal.confirm({
        title: '再提出年月一括登録の確認',
        content:
          '既に再提出年月が設定された受診者が含まれていますが、再提出年月を一括登録してよろしいですか？',
        onOk: openModal,
      });
    } else {
      openModal();
    }
  }, [selectedRowKeys, list, appModal, toast]);

  const handleOpenResubmissionForSingle = useCallback((rezeptId: number) => {
    setResubmissionRezeptIds([rezeptId]);
    setViolatingPatients([]);
    setServerError('');
    setResubmissionModalOpen(true);
  }, []);

  const handleResubmissionSubmit = useCallback(
    (yearmonth: string) => {
      setViolatingPatients([]);
      setServerError('');

      updateResubmissionMutation({
        rezept_ids: resubmissionRezeptIds,
        rezept_yearmonth: yearmonth,
      })
        .then(() => {
          setResubmissionModalOpen(false);
          setSelectedRowKeys([]);
        })
        .catch(err => {
          if (err instanceof AxiosError && err.response?.status === 422) {
            const errors = err.response.data?.errors;
            if (errors?.violating_patients) {
              setViolatingPatients(errors.violating_patients);
            }
            if (errors?.rezept_yearmonth?.[0]) {
              setServerError(errors.rezept_yearmonth[0]);
            }
          }
        });
    },
    [resubmissionRezeptIds, updateResubmissionMutation]
  );

  const handleCancelReturn = useCallback(() => {
    if (selectedRowKeys.length === 0) {
      appModal.warning({ title: ALERT_NO_SELECTION });
      return;
    }

    appModal.confirm({
      title: '返戻取り消しの確認',
      content: '現在チェックしているレセプトの返戻を取り消します。実行してよろしいですか？',
      onOk: () => {
        cancelReturnMutation({ rezept_ids: selectedRowKeys }).then(() => {
          setSelectedRowKeys([]);
        });
      },
    });
  }, [selectedRowKeys, appModal, cancelReturnMutation, toast]);

  const handleAddReturn = useCallback(
    (rezeptIds: number[]) => {
      markReturnMutation({ rezept_ids: rezeptIds }).then(() => {
        setAddReturnModalOpen(false);
        setSelectedRowKeys([]);
      });
    },
    [markReturnMutation]
  );

  return (
    <div className="flex flex-col gap-4">
      <CollapsibleCard title="返戻処理">
        <div className="p-2 mb-3 text-sm border rounded bg-gray-50">
          <div>確定していない状態にある返戻処理済みのレセプトを表示しています。</div>
          <div>
            ※再提出年月を指定し、レセプト発行メニューで『確定』処理を行うとこの一覧から表示が消えます。
          </div>
        </div>
        <div className="flex flex-wrap justify-between gap-2">
          <AppButton type="primary" onClick={handleBulkResubmission}>
            再提出年月一括登録
          </AppButton>
          <div className="flex gap-2">
            <AppButton type="primary" onClick={() => setAddReturnModalOpen(true)}>
              返戻を追加
            </AppButton>
            <AppButton type="primary" onClick={handleCancelReturn}>
              返戻処理取消
            </AppButton>
          </div>
        </div>
      </CollapsibleCard>

      <CollapsibleCard>
        <ReturnRezeptTable
          list={list}
          isLoading={isLoading}
          selectedRowKeys={selectedRowKeys}
          onToggleRow={handleToggleRow}
          onSelectAll={handleSelectAll}
          onOpenResubmissionModal={handleOpenResubmissionForSingle}
        />
      </CollapsibleCard>

      <ResubmissionMonthModal
        isOpen={resubmissionModalOpen}
        onClose={() => setResubmissionModalOpen(false)}
        onSubmit={handleResubmissionSubmit}
        isSubmitting={resubmitPending}
        violatingPatients={violatingPatients}
        serverError={serverError}
      />

      <AddReturnModal
        isOpen={addReturnModalOpen}
        onClose={() => setAddReturnModalOpen(false)}
        onAddReturn={handleAddReturn}
        isAdding={markPending}
      />
    </div>
  );
}
